import { TextField, Typography } from '@mui/material';
import BarcodeScannerModal, { DATA_MATRIX_FORMATS } from 'components/BarcodeScannerModal/BarcodeScannerModal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { Check, X } from 'lucide-react';
import React, { FC, useRef, useState } from 'react';
import batchService from 'services/api/batch.service';
import { ISgtinScanValidation } from '@core/models/sgtin-scan.model';

interface Props {
    productId: number;
    disabled?: boolean;
    /** Serials already added to this batch (to flag in-batch duplicates client-side). */
    existingSerials: string[];
    onValidSerial: (serial: string, validation: ISgtinScanValidation) => void;
}

const CheckRow: FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-xs">
        {ok ? <Check size={14} className="text-green-600" /> : <X size={14} className="text-red-500" />}
        <span className={ok ? 'text-green-800' : 'text-red-700'}>{label}</span>
    </div>
);

/**
 * Scan or paste a GS1 DataMatrix (USB wedge or camera) for an RX package. Validates the SGTIN
 * against the selected product on the server and shows every check. A serial can only
 * be added to the batch when all checks pass.
 */
const PackageDataMatrixScanField: FC<Props> = ({ productId, disabled, existingSerials, onValidSerial }) => {
    const [rawScan, setRawScan] = useState('');
    const [validating, setValidating] = useState(false);
    const [result, setResult] = useState<ISgtinScanValidation | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const duplicateInBatch =
        !!result?.serial && existingSerials.some((s) => s.trim() === result.serial?.trim());

    const canAdd = !!result?.valid && !duplicateInBatch;

    const commitSerial = (validation: ISgtinScanValidation) => {
        if (!validation.valid || !validation.serial) return;
        if (existingSerials.some((s) => s.trim() === validation.serial?.trim())) return;
        onValidSerial(validation.serial, validation);
        setRawScan('');
        setResult(null);
        inputRef.current?.focus();
    };

    const validate = async (value: string, options?: { autoAdd?: boolean }) => {
        const scan = value.trim();
        if (!scan) return;
        setValidating(true);
        try {
            const res = await batchService.validateSgtinScan(productId, scan);
            setResult(res.data);
            if (options?.autoAdd && res.data.valid && res.data.serial) {
                commitSerial(res.data);
            }
        } catch {
            setResult({
                valid: false,
                syntaxOk: false,
                checkDigitOk: false,
                productGtinMatchOk: false,
                duplicateInDb: false,
                errors: ['Could not validate scan. Try again.'],
            });
        } finally {
            setValidating(false);
        }
    };

    const addSerial = () => {
        if (!result) return;
        commitSerial(result);
    };

    const handleCameraScan = (code: string) => {
        const trimmed = code.trim();
        setRawScan(trimmed);
        setShowCamera(false);
        void validate(trimmed, { autoAdd: true });
    };

    return (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <Typography variant="subtitle2" className="font-semibold">
                Scan package (DSCSA)
            </Typography>
            <p className="mb-2 text-xs text-slate-500">
                Click this field and scan with a USB barcode gun (no camera needed), or paste the GS1 element
                string. Example: <span className="font-mono">(01)GTIN(21)serial(17)YYMMDD(10)lot</span>. USB
                scanners usually send Enter — the serial is validated and added automatically.
            </p>
            <div className="flex items-start gap-2">
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    size="small"
                    label="GS1 scan (01..21..17..10..)"
                    placeholder="(01)00312345678905(21)ABC123(17)271231(10)LOT1"
                    value={rawScan}
                    disabled={disabled || validating}
                    onChange={(e) => setRawScan(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            void validate(rawScan, { autoAdd: true });
                        }
                    }}
                />
                <button
                    type="button"
                    onClick={() => void validate(rawScan)}
                    disabled={disabled || validating || !rawScan.trim()}
                    className="mt-0.5 whitespace-nowrap rounded border border-slate-300 bg-white px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
                >
                    {validating ? 'Checking…' : 'Validate'}
                </button>
                <ButtonBase
                    type="button"
                    innerText="Open camera"
                    onClick={() => setShowCamera(true)}
                    disabled={disabled || validating}
                    className="mt-0.5 shrink-0 rounded border-none bg-emerald-600 px-3 py-2 text-xs text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                />
            </div>

            <BarcodeScannerModal
                open={showCamera}
                onClose={() => setShowCamera(false)}
                onScan={handleCameraScan}
                formats={DATA_MATRIX_FORMATS}
                title="Scan package DataMatrix"
                subtitle="Point camera at the GS1 DataMatrix on the package"
                qrbox={{ width: 250, height: 250 }}
                aspectRatio={1}
                scannerElementId="datamatrix-scanner-root"
                showScanLine={false}
            />

            {result && (
                <div className="mt-3 space-y-1.5">
                    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                        <CheckRow ok={result.syntaxOk} label="Syntax (GTIN-14 + serial ≤ 20)" />
                        <CheckRow ok={result.checkDigitOk} label="GS1 check digit" />
                        <CheckRow ok={result.productGtinMatchOk} label="Matches product GTIN" />
                        <CheckRow ok={!result.duplicateInDb && !duplicateInBatch} label="Not a duplicate serial" />
                    </div>

                    <div className="mt-2 rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
                        <div>
                            <span className="text-slate-500">GTIN:</span> {result.gtin || '—'}{' '}
                            <span className="ml-3 text-slate-500">Serial:</span> {result.serial || '—'}
                        </div>
                        <div>
                            <span className="text-slate-500">Lot:</span> {result.lot || '—'}{' '}
                            <span className="ml-3 text-slate-500">Expiry:</span> {result.expiry || '—'}
                        </div>
                        {result.sgtinUrn && (
                            <div className="break-all">
                                <span className="text-slate-500">SGTIN:</span> {result.sgtinUrn}
                            </div>
                        )}
                    </div>

                    {result.errors?.length > 0 && (
                        <ul className="list-inside list-disc text-xs text-red-700">
                            {result.errors.map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                        </ul>
                    )}
                    {duplicateInBatch && (
                        <p className="text-xs text-red-700">Serial already added to this batch.</p>
                    )}

                    <button
                        type="button"
                        onClick={addSerial}
                        disabled={!canAdd}
                        className="mt-1 rounded bg-[#bd902e] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#a87e26] disabled:opacity-50"
                    >
                        Add serial to batch
                    </button>
                </div>
            )}
        </div>
    );
};

export default PackageDataMatrixScanField;
